import type { AuthUser } from '@/stores/auth-store';

/**
 * Which locations a user may see and edit.
 *
 * One rule, used by the header location picker and by the product form's
 * inventory tables so the two can't disagree — the products service applies
 * the same rule server-side (src/lib/location-access.ts there), and it is the
 * server's answer that actually protects the data.
 *
 * - owner / super_admin: every location in the tenant.
 * - admin with no explicit assignment: every location (the historic
 *   tenant-wide admin — narrowing it would lock existing admins out).
 * - everyone else: only the locations granted via user.locationIds.
 */
export function seesAllLocations(user: Pick<AuthUser, 'role' | 'locationIds'> | null | undefined): boolean {
  if (!user) return true;
  if (user.role === 'owner' || user.role === 'super_admin') return true;
  return user.role === 'admin' && (user.locationIds ?? []).length === 0;
}

/** True when `locationId` is inside the user's grants. */
export function canAccessLocation(
  user: Pick<AuthUser, 'role' | 'locationIds'> | null | undefined,
  locationId: string,
): boolean {
  if (seesAllLocations(user)) return true;
  return (user?.locationIds ?? []).includes(locationId);
}

/** Filters a list of `{ id }` locations down to the ones the user may see. */
export function accessibleLocations<T extends { id: string }>(
  user: Pick<AuthUser, 'role' | 'locationIds'> | null | undefined,
  locations: T[],
): T[] {
  if (seesAllLocations(user)) return locations;
  const granted = new Set(user?.locationIds ?? []);
  return locations.filter((l) => granted.has(l.id));
}
